import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { Toaster } from 'react-hot-toast';
import { useLearner, useLearnerAnalytics } from '../../services/learners';
import { useImageUrl } from '../../services/uploads';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLearnerAttributes } from '../../services/settings';
import LearnerCourseRoster, {
  type LearnerCourseRow,
} from '../../components/learners/LearnerCourseRoster';
import RemindLearnerDialog from '../../components/learners/RemindLearnerDialog';
import LearnerEditModal from '../../components/learners/LearnerEditModal';
import { phantomProps } from '@/config/phantom';
import { parseApiDate } from '../../utils/apiDates';
import { formatLastLogin, lastLoginTitle } from '../../utils/lastLogin';

// ── Info row icons ────────────────────────────────────────────────────────────
const PersonIcon = () => (
  <svg width="14" height="17" viewBox="0 0 14 17" fill="none" className="flex-shrink-0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 8a3.5 3.5 0 100-7 3.5 3.5 0 000 7z" />
    <path d="M1 16c0-3.314 2.686-6 6-6s6 2.686 6 6" />
  </svg>
);
const EmailIcon = () => (
  <svg width="17" height="15" viewBox="0 0 17 15" fill="none" className="flex-shrink-0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="1" width="15" height="13" rx="1.5" />
    <path d="M1 3.5l7.5 5 7.5-5" />
  </svg>
);
const LocationIcon = () => (
  <svg width="17" height="18" viewBox="0 0 17 18" fill="none" className="flex-shrink-0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 1C5.462 1 3 3.462 3 6.5c0 4.375 5.5 10.5 5.5 10.5S14 10.875 14 6.5C14 3.462 11.538 1 8.5 1z" />
    <circle cx="8.5" cy="6.5" r="2" />
  </svg>
);
const DeptIcon = () => (
  <svg width="19" height="18" viewBox="0 0 19 18" fill="none" className="flex-shrink-0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="7" width="16" height="10" rx="1" />
    <path d="M5.5 7V5a4 4 0 018 0v2" />
    <line x1="9.5" y1="11" x2="9.5" y2="13" />
  </svg>
);
const RoleIcon = () => (
  <svg width="17" height="15" viewBox="0 0 17 15" fill="none" className="flex-shrink-0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="1" width="15" height="13" rx="2" />
    <circle cx="5.5" cy="5.5" r="1.5" />
    <path d="M1 10.5h4a2.5 2.5 0 005 0h6" />
  </svg>
);
const ClockIcon = () => (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" className="flex-shrink-0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8.5" cy="8.5" r="7" />
    <path d="M8.5 4.5v4l2.5 1.5" />
  </svg>
);

// ── Info row ──────────────────────────────────────────────────────────────────
const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string; title?: string }> = ({ icon, label, value, title }) => (
  <div className="flex items-center gap-3 min-w-0">
    <span className="text-text-secondary flex-shrink-0">{icon}</span>
    <span className="text-sm font-medium text-text-primary w-28 flex-shrink-0">{label}:</span>
    <span className="text-sm text-text-primary truncate" title={title}>{value || '—'}</span>
  </div>
);

// ── Avatar placeholder ────────────────────────────────────────────────────────
const AvatarFallback: React.FC<{ name: string; size: number }> = ({ name, size }) => {
  const initials = name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div
      className="rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <span className="font-bold text-primary-600 dark:text-primary-200" style={{ fontSize: size * 0.3 }}>{initials}</span>
    </div>
  );
};

// ── Analytics KPI skeleton / real grid ───────────────────────────────────────
// Extracted to avoid phantom-ui type issues when used inside && JSX expressions
const AnalyticsKpiSection: React.FC<{
  analyticsLoading: boolean;
  analytics: Record<string, unknown> | null | undefined;
  isDark: boolean;
  /** Which roster the two course cards are showing, if any. */
  openCard: 'assigned' | 'completed' | null;
  onToggleCard: (card: 'assigned' | 'completed') => void;
}> = ({ analyticsLoading, analytics, isDark, openCard, onToggleCard }) => {
  const stats = (analytics?.completion_stats ?? {}) as Record<string, unknown>;
  const scores = (analytics?.scores ?? {}) as Record<string, unknown>;
  // The two course cards open the courses behind them; the other two are
  // summaries with nothing to list (Aleix, 2026-09-15).
  const kpiData: {
    label: string;
    value: string;
    card?: 'assigned' | 'completed';
  }[] = [
    { label: 'Courses Assigned', value: String(stats.courses_assigned || 0), card: 'assigned' },
    { label: 'Courses Completed', value: String(stats.courses_completed || 0), card: 'completed' },
    { label: 'Completion Rate', value: `${stats.completion_rate || 0}%` },
    { label: 'Avg Score', value: `${scores.avg_score || 0}%` },
  ];

  return (
    <phantom-ui {...phantomProps(isDark, analyticsLoading)}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpiData.map(kpi => {
          if (!kpi.card) {
            return (
              <div key={kpi.label} className="card p-5">
                <p className="text-xs text-text-secondary mb-1">{kpi.label}</p>
                <p className="text-2xl font-bold text-text-primary">{kpi.value}</p>
              </div>
            );
          }
          const open = openCard === kpi.card;
          return (
            <button
              key={kpi.label}
              type="button"
              onClick={() => onToggleCard(kpi.card!)}
              aria-expanded={open}
              data-testid={`kpi-${kpi.card}`}
              className={`card p-5 text-left cursor-pointer transition-[border-color,background-color,box-shadow] duration-150 focus-ring hover:border-primary-500/50 hover:shadow-md active:scale-[0.99] ${
                open ? 'border-primary-500/60 bg-primary-500/[0.04]' : ''
              }`}
            >
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="text-xs text-text-secondary">{kpi.label}</p>
                <ChevronDownIcon
                  className={`h-4 w-4 shrink-0 text-primary-500 transition-transform duration-200 ${
                    open ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </div>
              <p className="text-2xl font-bold text-text-primary">{kpi.value}</p>
              {/* Nothing said these were clickable (Aleix, 2026-09-15). */}
              <p className="mt-1 text-[11px] font-semibold text-primary-600 dark:text-primary-400">
                {open ? 'Hide courses' : 'View courses'}
              </p>
            </button>
          );
        })}
      </div>
    </phantom-ui>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const LearnerDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeMembership } = useAuth();

  const [activeTab, setActiveTab] = useState<'profile' | 'courses'>('profile');
  // The roster opens from the card it belongs to rather than sitting below as
  // a third block (Aleix, 2026-09-15).
  const [openCard, setOpenCard] = useState<'assigned' | 'completed' | null>(null);
  // Seeing that someone is behind should come with something you can do about
  // it (Aleix, 2026-09-21).
  const [remindRow, setRemindRow] = useState<LearnerCourseRow | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const { data: learner, isLoading, error } = useLearner(id);
  const { data: analytics, isLoading: analyticsLoading } = useLearnerAnalytics(id);
  const [avatarUrl] = useImageUrl(learner?.avatar_url ?? null);

  const { data: departments } = useLearnerAttributes('departments', { per_page: 200 });
  const { data: roles } = useLearnerAttributes('roles', { per_page: 200 });
  const { data: locations } = useLearnerAttributes('locations', { per_page: 200 });

  const departmentName = learner?.department_id
    ? (departments?.data.find(d => d.id === learner.department_id)?.name ?? '—')
    : '—';
  const roleName = learner?.role_id
    ? (roles?.data.find(r => r.id === learner.role_id)?.name ?? '—')
    : '—';
  const locationName = learner?.location_id
    ? (locations?.data.find(l => l.id === learner.location_id)?.name ?? '—')
    : '—';

  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const userRole = activeMembership?.role;
  const canEdit = userRole === 'Admin' || userRole === 'Owner';

  // ── Error ──────────────────────────────────────────────────────────────────
  if (!isLoading && (error || !learner)) {
    return (
      <div className="card p-8 text-center space-y-4">
        <p className="text-text-primary font-semibold">Could not load learner</p>
        <p className="text-sm text-text-secondary">{error?.message || 'Learner not found'}</p>
        <button onClick={() => navigate('/learners')} className="btn-primary px-6">
          Back to Learners
        </button>
      </div>
    );
  }

  const fullName = learner ? `${learner.first_name} ${learner.last_name}` : 'Full Name';

  return (
    <motion.div {...slideUp} className="space-y-6">
      <Toaster position="top-right" />

      {/* ── Profile section wrapped in phantom-ui ── */}
      <phantom-ui {...phantomProps(isDark, isLoading)}>
        {/* ── Breadcrumb ── */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5">
          <Link to="/dashboard" className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350">Dashboard</Link>
          <span className="body-small text-text-secondary">•</span>
          <Link to="/learners" className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350">Learners</Link>
          <span className="body-small text-text-secondary">•</span>
          <span className="body-small text-primary-600 dark:text-primary-350 font-medium truncate max-w-[240px]">{fullName}</span>
        </nav>

        {/* ── Profile banner card ── */}
        <div className="card">
          {/* Cover image — overflow-hidden only here so avatar can escape */}
          <div className="h-[200px] md:h-[240px] w-full relative overflow-hidden rounded-t-[inherit]">
            <div
              aria-hidden="true"
              className="brand-gradient absolute inset-0 pointer-events-none"
            />
          </div>

          {/* Avatar floats over the cover/card boundary */}
          <div className="relative px-6">
            <div className="absolute -top-12 md:-top-16 left-0 md:left-6 w-full md:w-auto flex md:block justify-center md:justify-start">
              <div className="border-[3px] border-background-primary rounded-full overflow-hidden flex-shrink-0 w-24 h-24 md:w-32 md:h-32 shadow-md">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={fullName} className="w-full h-full object-cover" />
                ) : (
                  <AvatarFallback name={fullName} size={128} />
                )}
              </div>
            </div>

            {/* Name + role (left, beside the avatar) with the edit action on
                the opposite side */}
            <div className="pt-14 md:pt-4 pb-0 md:pl-40 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xl md:text-2xl font-bold text-text-primary leading-tight">{fullName}</p>
                <p className="text-sm text-text-secondary mt-0.5 opacity-60">
                  {learner?.is_active ? 'Active' : 'Inactive'}
                </p>
              </div>
              {canEdit && (
                <button
                  onClick={() => setShowEditModal(true)}
                  className="btn-primary px-5 text-sm flex-shrink-0"
                  data-testid="edit-btn"
                >
                  Update Learner
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="relative mt-3 px-4 flex items-end gap-0 border-t border-[rgba(189,188,201,0.2)]">
            <div className="absolute bottom-0 left-0 right-0 h-px bg-[rgba(189,188,201,0.15)]" />
            {(['profile', 'courses'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                  activeTab === tab
                    ? 'border-text-primary text-text-primary font-semibold'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                {tab === 'profile' && (
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" className="opacity-80">
                    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                  </svg>
                )}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </phantom-ui>

      {/* ── Tab content ── */}
      {activeTab === 'profile' && (
        <div className="card px-8 md:px-14 py-10">
          {/* Desktop: two columns. Mobile: single column */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-24 gap-y-6">
            <div className="space-y-6">
              <InfoRow icon={<PersonIcon />} label="Full name" value={fullName} />
              <InfoRow icon={<EmailIcon />} label="Email" value={learner?.email ?? '—'} />
              <InfoRow icon={<LocationIcon />} label="Location" value={locationName} />
            </div>
            <div className="space-y-6">
              <InfoRow icon={<DeptIcon />} label="Department" value={departmentName} />
              <InfoRow icon={<RoleIcon />} label="Role" value={roleName} />
              <InfoRow
                icon={<ClockIcon />}
                label="Last login"
                value={formatLastLogin(learner?.last_login_at)}
                title={lastLoginTitle(learner?.last_login_at)}
              />
            </div>
          </div>

          {/* Groups */}
          {learner?.groups && learner.groups.length > 0 && (
            <div className="mt-8 pt-6 border-t border-[rgba(189,188,201,0.2)]">
              <p className="text-xs font-medium text-text-secondary mb-3">Groups</p>
              <div className="flex flex-wrap gap-2">
                {learner.groups.map(g => (
                  <span
                    key={g.id}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-100 dark:border-primary-800"
                  >
                    {g.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'courses' && (
        <div className="space-y-4">
          {/* Analytics KPIs — wrapped in a separate component to avoid phantom-ui JSX type issues */}
          <AnalyticsKpiSection
            analyticsLoading={analyticsLoading}
            analytics={analytics as Record<string, unknown> | null | undefined}
            isDark={isDark}
            openCard={openCard}
            onToggleCard={(card) => setOpenCard((prev) => (prev === card ? null : card))}
          />

          {/* The courses behind whichever number was clicked. */}
          {!analyticsLoading && openCard && (
            <LearnerCourseRoster
              onRemind={setRemindRow}
              heading={openCard === 'completed' ? 'Courses completed' : 'Courses assigned'}
              courses={(() => {
                const all = Array.isArray((analytics as Record<string, unknown>)?.courses)
                  ? ((analytics as Record<string, unknown>).courses as LearnerCourseRow[])
                  : [];
                return openCard === 'completed'
                  ? all.filter((c) => c.status === 'completed')
                  : all;
              })()}
            />
          )}

          {/* Recent activity */}
          {analytics?.completion_stats &&
            Array.isArray((analytics.completion_stats as Record<string, unknown>).recent_activity) &&
            ((analytics.completion_stats as Record<string, unknown>).recent_activity as unknown[]).length > 0 && (
              <div className="card p-6">
                <h3 className="text-sm font-semibold text-text-primary mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {((analytics.completion_stats as Record<string, unknown>).recent_activity as Record<string, unknown>[]).map((activity, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-background-secondary">
                      <div>
                        <p className="text-sm font-medium text-text-primary">{String(activity.course_title)}</p>
                        <p className="text-xs text-text-secondary mt-0.5">
                          Completed {parseApiDate(String(activity.completed_at)).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-success-600">
                        {String(activity.score)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {!analyticsLoading && !analytics && (
            <div className="card p-8 text-center text-text-secondary text-sm">
              No course data available.
            </div>
          )}
        </div>
      )}

      {remindRow && id && (
        <RemindLearnerDialog
          learnerId={id}
          learnerName={learner?.first_name || learner?.full_name || 'this learner'}
          row={remindRow}
          onClose={() => setRemindRow(null)}
        />
      )}

      {/* ── Edit modal ── */}
      {showEditModal && id && (
        <LearnerEditModal
          learnerId={id}
          onClose={() => setShowEditModal(false)}
          onSaved={() => setShowEditModal(false)}
          onDeleted={() => navigate('/learners')}
        />
      )}
    </motion.div>
  );
};

export default LearnerDetailsPage;
